import Region from './Region';
import ImplantSource from './ImplantSource';
import { useImplantContext } from '../../../store/implant-context';
import EditCatalogNumbers from './EditCatalogNumbers';
import styles from './RegionalInfo.module.css';
import BackgroundContainer from '../../../components/ui-components/BackgroundContainer';
import MriStatus from '../MriStatus';
import Button, { ButtonType } from '../../general-components/Button';
import RichText from '../../general-components/RichText';
import CustomFields from '../../custom-fields/CustomFields';
import Model from '../Model';
import { getConditionalBorderColor } from './getConditionalBorderColor';

const regions = ['EU', 'US'];

const RegionalInfo = () => {
    const {
        regionalInfos,
        conditionals,
        addRegionalInfo,
        deleteRegionalInfo,
        duplicateRegionalInfo,
        updateRegionalInfoField,
    } = useImplantContext();

    const handleRegionChange = (regionalInfoIndex: number, region: string) => {
        const currentRegions = regionalInfos[regionalInfoIndex]?.regions || [];

        let updatedRegions;

        if (currentRegions.includes(region)) {
            updatedRegions = currentRegions.filter((r: string) => r !== region);
        } else {
            updatedRegions = [...currentRegions, region];
        }
        updateRegionalInfoField(regionalInfoIndex, 'regions', updatedRegions);
    };

    const handleMriStatusChange = (regionalInfoIndex: number, value: any) => {
        const status = parseInt(value);
        updateRegionalInfoField(regionalInfoIndex, 'mriStatus', status);
        if (status !== 2) {
            updateRegionalInfoField(regionalInfoIndex, 'conditionals', null);
        }
    };

    const addCustomField = (regionalInfoIndex: number) => {
        const currentCustomFields =
            regionalInfos[regionalInfoIndex]?.customFields || [];
        const updatedCustomFields = [
            ...currentCustomFields,
            { heading: '', value: '' },
        ];
        updateRegionalInfoField(
            regionalInfoIndex,
            'customFields',
            updatedCustomFields
        );
    };

    const updateCustomField = (
        regionalInfoIndex: number,
        fieldIndex: number,
        field: any
    ) => {
        const currentCustomFields =
            regionalInfos[regionalInfoIndex]?.customFields || [];
        const updatedCustomFields = [...currentCustomFields];
        updatedCustomFields[fieldIndex] = field;
        updateRegionalInfoField(
            regionalInfoIndex,
            'customFields',
            updatedCustomFields
        );
    };

    const deleteCustomField = (
        regionalInfoIndex: number,
        fieldIndex: number
    ) => {
        const currentCustomFields =
            regionalInfos[regionalInfoIndex]?.customFields || [];
        const updatedCustomFields = [...currentCustomFields];
        updatedCustomFields.splice(fieldIndex, 1);
        updateRegionalInfoField(
            regionalInfoIndex,
            'customFields',
            updatedCustomFields
        );
    };

    return (
        <div className={styles.container}>
            <div>
                <h3>
                    Regional Information ({regionalInfos?.length || 0} region
                    {(regionalInfos?.length || 0) !== 1 ? 's' : ''})
                </h3>
                <Button type={ButtonType.primary} onClick={addRegionalInfo}>
                    Add Regional Section
                </Button>
            </div>

            {regionalInfos?.map((regionalInfo: any, index: number) => {
                if (regionalInfo === undefined) {
                    return null;
                }
                // Find the matching conditional to get its actual id for color matching
                let conditionalId: string | number | undefined =
                    regionalInfo.conditionals;

                if (
                    conditionalId !== null &&
                    conditionalId !== undefined &&
                    conditionals
                ) {
                    // Try to find by id first (preferred), then _id, then fallback to index
                    const matchingConditional = conditionals.find(
                        (cond: any) =>
                            cond.id === conditionalId ||
                            cond._id === conditionalId ||
                            cond._id === String(conditionalId)
                    );

                    if (matchingConditional) {
                        // Use the conditional's id for consistent color matching
                        conditionalId =
                            matchingConditional.id ||
                            matchingConditional._id ||
                            conditionalId;
                    } else if (
                        typeof conditionalId === 'number' &&
                        conditionalId >= 0 &&
                        conditionalId < conditionals.length
                    ) {
                        // Fallback: if stored as index and within bounds, use that index's conditional
                        const conditionalAtIndex = conditionals[conditionalId];
                        conditionalId =
                            conditionalAtIndex.id ||
                            conditionalAtIndex._id ||
                            conditionalId;
                    }
                }

                const borderColor = getConditionalBorderColor(conditionalId);

                return (
                    <BackgroundContainer
                        className={styles.backgroundContainer}
                        key={index}
                        borderColor={borderColor}
                    >
                        <div className={styles.buttonGroup}>
                            <Button
                                type={ButtonType.secondary}
                                onClick={() => duplicateRegionalInfo(index)}
                            >
                                Duplicate Section
                            </Button>
                            {regionalInfos.length > 1 && (
                                <Button
                                    type={ButtonType.cancel}
                                    onClick={() => deleteRegionalInfo(index)}
                                >
                                    Remove Section
                                </Button>
                            )}
                        </div>
                        <div className={styles.regionalInfoContainer}>
                            <div className={styles.regionalInfo}>
                                <Model
                                    value={regionalInfo.model}
                                    onUpdateInfoField={(value: any) =>
                                        updateRegionalInfoField(
                                            index,
                                            'model',
                                            value
                                        )
                                    }
                                />

                                <Region
                                    availableRegions={regions}
                                    selectedRegions={regionalInfo.regions}
                                    onRegionChange={(region: string) =>
                                        handleRegionChange(index, region)
                                    }
                                />
                                <label className={styles.infoLabel}>
                                    Catalog/Model numbers
                                </label>
                                <EditCatalogNumbers index={index} />

                                <MriStatus
                                    mriStatusString="mriStatus"
                                    info={{ mriStatus: regionalInfo.mriStatus }}
                                    onUpdateInfoField={(
                                        field: string,
                                        value: any
                                    ) => handleMriStatusChange(index, value)}
                                />

                                <RichText
                                    label={'Precaution'}
                                    description={regionalInfo.precaution}
                                    onChange={(richText: any) => {
                                        updateRegionalInfoField(
                                            index,
                                            'precaution',
                                            richText
                                        );
                                    }}
                                />

                                <label className={styles.infoLabel}>
                                    Commercial Distribution End Date
                                </label>
                                <input
                                    name="distEndDate"
                                    type="date"
                                    value={
                                        regionalInfo.distEndDate !== '' &&
                                        regionalInfo.distEndDate !== null &&
                                        regionalInfo.distEndDate !== undefined
                                            ? new Date(regionalInfo.distEndDate)
                                                  .toISOString()
                                                  .split('T')[0]
                                            : ''
                                    }
                                    onChange={(e) =>
                                        updateRegionalInfoField(
                                            index,
                                            'distEndDate',
                                            e.target.value
                                        )
                                    }
                                />

                                <CustomFields
                                    customFields={regionalInfo.customFields}
                                    onAddField={() => addCustomField(index)}
                                    onUpdateField={(
                                        fieldIndex: number,
                                        field: any
                                    ) =>
                                        updateCustomField(
                                            index,
                                            fieldIndex,
                                            field
                                        )
                                    }
                                    onDeleteField={(fieldIndex: number) =>
                                        deleteCustomField(index, fieldIndex)
                                    }
                                />
                            </div>

                            <div className={styles.implantSource}>
                                <ImplantSource
                                    regionalInfoIndex={index}
                                    regionalInfo={regionalInfo}
                                    onUpdateRegionalInfoField={
                                        updateRegionalInfoField
                                    }
                                />
                            </div>
                        </div>
                    </BackgroundContainer>
                );
            })}
        </div>
    );
};

export default RegionalInfo;
